import React from 'react'
import Flex from 'typescript-styled-flex'
import { Trans, WithTranslation } from 'react-i18next'
import { Divider, IconButton, Menu, MenuItem } from '@material-ui/core'
import {
  FiCopy,
  FiCornerUpRight,
  FiMoreVertical,
  FiSave,
  FiSettings,
  FiTrash,
  FiUpload,
} from 'react-icons/fi'
import { useDispatch, useSelector } from 'react-redux'
import { ReservationModalWrapper } from '../../../components/Calendar/ReservationModal'
import {
  CloseButton,
  ModalFlex,
  ModalHeader,
} from '../../../components/Template/Modal'
import SpinningIcon from '../../../components/Template/SpinningIcon'
import Button from '../../../components/Template/Button'
import { Desktop, is, Mobile } from '../../../components/MediaQuery'
import { driver, names } from '../../../database/localStorage'
import { FormNames } from '../../Form/CreateReservation'
import { Loader } from '../../../components/Loader/CalendarLoader'
import { Modals } from '../../Modals/ModalContainer'
import { BBSlider } from '../FilterBar/FilterBar'
import useSize from '../../../hooks/useSize'
import setSize from '../../../actions/bookingCalendar/calendar/setSize'
import { IStore } from '../../../reducers'
import DeleteModal from './DeleteModal'
import ReservationModalHeader from './Components/ReservationModalHeader'
import ExportReservation from './Components/ExportReservation'
import Badges from './Components/Badges'
import {
  CloseModal,
  ReservationConnectedCopyModal as CopyModal,
  ReservationConnectedTransferModal as TransferModal,
} from './index'
import validateReservation from '../../../actions/bookingCalendar/reservationModal/validateReservationForm'
import { Mixpanel } from '../../../utils/Mixpanel'
import { getUserId } from '../../../reducers/userAccount/selectors/userAccount'
import {
  getModalCreateReservationIsOpen,
  getModalUpdateReservationIsOpen,
} from '../../../reducers/bookingCalendar/selectors/getModalOpen'
import useDebouncedEffect from 'use-debounced-effect'

function ReservationModal({
  children,
  open,
  toggleConfigurationModal,
  onClose,
  deleteEvent,
  type,
  isLoading,
  isSubmitting,
  isSaving,
  airbnb,
  rawEvent,
  isValidating,
  submitForm,
  booking,
  isDirty,
  t,
  errors,
  fireError,
  toggleCloseModal,
  modalOpen,
  hideModal,
  modalClose,
  allowReservationEdit,
}: { children?: any } & IReservationModalProps &
  WithTranslation &
  IReservationModalDispatch) {
  const [menu, toggleMenu] = React.useState(false)
  const size = useSize()
  const userId = useSelector(getUserId)
  const dispatch = useDispatch()

  const updateOpen = useSelector(getModalUpdateReservationIsOpen)
  const createOpen = useSelector(getModalCreateReservationIsOpen)

  const formOpen = updateOpen || createOpen
  const showSlider = useSelector(
    (state: IStore) => state.bookingCalendar.size.showSliders,
  )

  function setReservationSize(val) {
    dispatch(setSize(val, 'forms'))
  }

  function toggleConfiguration() {
    Mixpanel.track('Reservation Add Another Field Menu Item Used', {
      'Reservation ID': rawEvent && Number(rawEvent.id),
    })
    toggleConfigurationModal()
  }

  useDebouncedEffect(
    () => {
      if (formOpen && open) {
        if (updateOpen) {
          Mixpanel.track('Reservation Form View', {
            'Tabs Form View?': true,
            'Reservation ID': rawEvent && Number(rawEvent.id),
          })
          Mixpanel.people.increment(userId, 'Opened Reservations', 1)
        } else if (createOpen) {
          Mixpanel.track('New Reservation View', {
            'Tabs Form View?': true,
          })
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    2000,
    [formOpen, createOpen, updateOpen, open, userId],
  )

  const menuAnchor = React.useRef(null)

  const isDesktop = is.desktop()

  const ModalDelete = (
    <DeleteModal
      eventId={rawEvent && rawEvent.id}
      close={modalClose}
      closeModal={modalClose}
      onDelete={deleteEvent}
    />
  )

  function expandMenu() {
    toggleMenu(true)
  }

  const toggleCopyModal = () => {
    Mixpanel.track('Reservation Copy Menu Item Used', {
      'Reservation ID': rawEvent && Number(rawEvent.id),
    })

    modalOpen(
      Modals.CopyModal,
      <CopyModal onCopySuccess={onClose} close={modalClose} />,
    )
  }

  const toggleTransferModal = () => {
    Mixpanel.track('Reservation Move Menu Item Used', {
      'Reservation ID': rawEvent && Number(rawEvent.id),
    })
    modalOpen(
      Modals.TransferModal,
      <TransferModal close={modalClose} onTransferSuccess={onClose} />,
    )
  }

  function resize() {
    toggleMenu(false)
  }

  React.useEffect(() => {
    window.addEventListener('resize', resize)
    return () => {
      window.removeEventListener('resize', resize)
    }
  })

  function submit() {
    if (errors) {
      const errorTabs = new Set<number>()
      if (errors.dateTo && errors.dateFrom) {
        fireError(
          t('reservation_reserved_date_error'),
          'reservationDateReservedError',
        )
        errorTabs.add(0)
      } else if (errors.dateTo && !errors.dateFrom) {
        fireError(t('reservation_no_date_error'), 'reservationDateEmptyError')
        errorTabs.add(0)
      } else if (errors.adultsCount) {
        fireError(
          t('reservation_error_wrong_number_of_people'),
          'reservationPeopleNumberError',
        )
        errorTabs.add(0)
      } else if (errors.roomId) {
        fireError(t('BBO_963_select_room'), 'reservationNoRoomError')
        errorTabs.add(0)
      } else if (errors.email) {
        fireError(t('reg_error_email_not_email'), 'reservationEmailError')
        errorTabs.add(1)
      } else if (errors.guest && errors.guest.email) {
        fireError(t('reg_error_email_not_email'), 'reservationEmailError')
        errorTabs.add(1)
      }
      dispatch(validateReservation([...errorTabs]))
    }
    submitForm()
  }

  function onDelete() {
    Mixpanel.track('Reservation Delete Menu Item Used', {
      'Reservation ID': rawEvent && Number(rawEvent.id),
    })
    modalOpen(Modals.ReservationDeleteModal, ModalDelete)
    toggleMenu(false)
  }

  function closeMenu() {
    toggleMenu(false)
  }

  function close() {
    const dontShowAgain = driver.getItem(
      names.UserAccountStorage.DontShowAgainCloseModal,
      '',
    )

    if (isDirty && !dontShowAgain) {
      toggleCloseModal()
    } else {
      if (isDirty) {
        Mixpanel.track('Reservation Form Abandoned', {
          'Reservation ID': rawEvent && Number(rawEvent.id),
          'Tabs Form View?': true,
        })
        Mixpanel.people.increment(userId, 'Reservation Form Abandonments', 1)
      }
      onClose()
    }
    modalClose(Modals.ReservationDeleteModal)
  }

  function value(val) {
    return `${val}%`
  }

  function changeSize(e, val) {
    setReservationSize(Number(val) / 100)
  }

  return (
    <ModalFlex
      open={open}
      style={{
        zIndex: hideModal ? -1 : 1300,
        visibility: hideModal ? 'hidden' : 'unset',
      }}
      hideBackdrop={hideModal}
      disableBackdropClick={hideModal}
      onClose={close}
    >
      {!isLoading ? (
        <ReservationModalWrapper
          style={isDesktop ? { maxWidth: '1200px' } : {}}
        >
          <ModalHeader
            style={{
              alignItems: 'start',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Flex
              full={true}
              style={{
                minWidth: '0',
                padding: isDesktop
                  ? `${15 * size}px ${9 * size}px 0 ${25 * size}px`
                  : `${15 * size}px 0 0 ${16 * size}px`,
              }}
            >
              <Flex
                style={{ minWidth: '0', width: '100%' }}
                alignStart={true}
                column={!isDesktop}
                justifyStart={true}
              >
                {type === 'update' ? (
                  <ReservationModalHeader.Update />
                ) : (
                  <ReservationModalHeader.Create />
                )}
              </Flex>
              <Flex justifyEnd={true} alignCenter={true}>
                <Desktop>
                  {showSlider && (
                    <div
                      style={{
                        width: '250px',
                      }}
                    >
                      <BBSlider
                        defaultValue={size * 100}
                        valueLabelFormat={value}
                        valueLabelDisplay="auto"
                        step={5}
                        onChangeCommitted={changeSize}
                        marks={true}
                        min={50}
                        max={120}
                      />
                    </div>
                  )}
                  {type === 'update' && (
                    <ExportReservation
                      loadingElement={
                        <IconButton style={{ marginRight: '10px' }}>
                          <SpinningIcon size={18 * size} />
                        </IconButton>
                      }
                    >
                      <IconButton style={{ marginRight: '10px' }}>
                        <FiUpload size={18 * size} />
                      </IconButton>
                    </ExportReservation>
                  )}

                  <IconButton
                    style={{ marginRight: '10px' }}
                    onClick={!isSaving && toggleConfiguration}
                  >
                    <FiSettings size={18 * size} />
                  </IconButton>

                  {allowReservationEdit && (
                    <IconButton
                      onClick={!isSaving && toggleCopyModal}
                      style={{ marginRight: '10px' }}
                    >
                      <FiCopy size={18 * size} />
                    </IconButton>
                  )}

                  {type === 'update' && !airbnb && allowReservationEdit && (
                    <React.Fragment>
                      <IconButton
                        style={{ marginRight: '10px' }}
                        onClick={!isSaving && toggleTransferModal}
                      >
                        <FiCornerUpRight size={18 * size} />
                      </IconButton>

                      {(booking === '0' || booking === '') && (
                        <IconButton
                          style={{ marginRight: '15px' }}
                          onClick={!isSaving && onDelete}
                        >
                          <FiTrash color={'red'} size={18 * size} />
                        </IconButton>
                      )}
                    </React.Fragment>
                  )}

                  {allowReservationEdit && (
                    <Button
                      size={size}
                      as={'button'}
                      type={'submit'}
                      onClick={submit}
                      primary={true}
                      disabled={isSubmitting || isValidating || isSaving}
                    >
                      <div style={{ marginRight: '5px' }}>
                        {isSubmitting || isValidating || isSaving ? (
                          <SpinningIcon />
                        ) : (
                          <FiSave />
                        )}
                      </div>
                      <Trans i18nKey={'save'}>Zapisz</Trans>
                    </Button>
                  )}

                  <CloseButton
                    onClose={close}
                    style={{ transform: `scale(${size})` }}
                  />
                </Desktop>
                <Mobile>
                  <div ref={menuAnchor} style={{ marginLeft: '10px' }} />
                  <IconButton onClick={expandMenu}>
                    <FiMoreVertical />
                  </IconButton>

                  <Menu
                    anchorEl={menuAnchor.current}
                    open={menu}
                    onClose={closeMenu}
                    onClick={closeMenu}
                  >
                    {type === 'update' && (
                      <ExportReservation
                        loadingElement={
                          <MenuItem>
                            <SpinningIcon
                              size={18}
                              style={{ marginRight: '10px' }}
                            />
                            <Trans i18nKey={'menu_export'} />
                          </MenuItem>
                        }
                      >
                        <MenuItem>
                          <FiUpload size={18} style={{ marginRight: '10px' }} />
                          <Trans i18nKey={'menu_export'} />
                        </MenuItem>
                      </ExportReservation>
                    )}
                    <MenuItem onClick={toggleConfiguration}>
                      <FiSettings size={18} style={{ marginRight: '10px' }} />
                      <Trans i18nKey={'configuration'} />
                    </MenuItem>

                    {allowReservationEdit && (
                      <MenuItem onClick={toggleCopyModal}>
                        <FiCopy size={18} style={{ marginRight: '10px' }} />
                        <Trans i18nKey={'reservation_copy_btn'} />
                      </MenuItem>
                    )}
                    {type === 'update' && !airbnb && allowReservationEdit && (
                      <React.Fragment>
                        <MenuItem onClick={toggleTransferModal}>
                          <FiCornerUpRight
                            style={{ marginRight: '10px' }}
                            size={18}
                          />
                          <Trans i18nKey={'reservation_move_btn'} />
                        </MenuItem>
                        {(booking === '0' || booking === '') &&
                          allowReservationEdit && (
                            <MenuItem onClick={onDelete}>
                              <FiTrash
                                style={{ marginRight: '10px' }}
                                color={'red'}
                                size={18}
                              />
                              <Trans i18nKey={'delete_reservation'} />
                            </MenuItem>
                          )}
                      </React.Fragment>
                    )}
                  </Menu>
                  <CloseButton onClose={close} />
                </Mobile>
              </Flex>
            </Flex>
            <Flex
              full={true}
              style={{
                padding: isDesktop ? `0 ${25 * size}px` : `0 ${16 * size}px`,
              }}
            >
              {type === 'create' ? <Badges.Create /> : <Badges.Update />}
            </Flex>
          </ModalHeader>

          {open && !isLoading && React.cloneElement(children, {})}

          <CloseModal
            type={type === 'update' ? FormNames.Update : FormNames.Create}
          />

          <Mobile>
            <Flex style={{ padding: `${15 * size}px` }} column={true}>
              <Divider
                style={{ margin: `${15 * size}px 0px`, width: '100%' }}
              />
              <Flex full={true} row={true}>
                <Button
                  size={size}
                  onClick={close}
                  light={true}
                  style={{
                    height: `${38 * size}px`,
                    width: allowReservationEdit ? '50%' : '100%',
                  }}
                >
                  <Trans i18nKey={'application_block_dialog_close_btn'} />
                </Button>
                {allowReservationEdit && (
                  <Button
                    size={size}
                    style={{ height: `${38 * size}px`, width: '50%' }}
                    as={'button'}
                    type={'submit'}
                    primary={true}
                    disabled={isSubmitting || isValidating || isSaving}
                    onClick={submit}
                  >
                    <div
                      style={{
                        fontSize: `${16 * size}px`,
                        marginRight: `${5 * size}px`,
                      }}
                    >
                      {isSubmitting || isValidating || isSaving ? (
                        <SpinningIcon size={`${16 * size}px`} />
                      ) : (
                        <FiSave size={`${16 * size}px`} />
                      )}
                    </div>
                    <Trans i18nKey={'save'}>Zapisz</Trans>
                  </Button>
                )}
              </Flex>
            </Flex>
          </Mobile>
        </ReservationModalWrapper>
      ) : (
        <ReservationModalWrapper
          style={
            isDesktop
              ? { alignItems: 'center', display: 'flex', maxWidth: '1200px' }
              : { alignItems: 'center', display: 'flex' }
          }
        >
          <Loader />
        </ReservationModalWrapper>
      )}
    </ModalFlex>
  )
}

export default ReservationModal

export interface IReservationModalProps {
  open: boolean
  type: 'create' | 'update'
  isLoading: boolean
  isSubmitting: boolean
  isValidating: boolean
  isDirty: boolean
  errors: any
  airbnb?: string
  isSaving: boolean
  booking?: string
  transferModal?: boolean
  deleteModal?: boolean
  rawEvent?: any
  userId?: any
  hideModal?: boolean
  allowReservationEdit?: boolean
}

export interface IReservationModalDispatch {
  onClose: () => void
  deleteEvent?: () => void
  toggleCloseModal: () => void
  toggleConfigurationModal: () => void
  submitForm: () => void
  fireError: (message, id) => void
  setTransferModal?: (toggle: boolean) => void
  toggleDeleteModal?: (toggle: boolean) => void
  modalOpen?: (id, element) => void
  modalClose?: (id) => void
}
